import {
  ISeriesPrimitive,
  SeriesAttachedParameter,
  Time,
  IPrimitivePaneRenderer,
  IPrimitivePaneView,
  PrimitivePaneViewZOrder,
  MismatchDirection,
} from 'lightweight-charts';
import {
  LAYOUT_PRIORITY,
  MarkerLayoutClient,
  type LayoutContext,
  type LayoutItem,
  type MarkerLayoutRegistry,
} from './markerLayout';

/**
 * Absorption-at-level markers: a dot under the candle (bullish, sellers
 * absorbed at support) or above it (bearish, buyers absorbed at resistance)
 * with the level tag beside it, "ABS PW".
 *
 * Items arrive already folded into the chart's timeframe (`time` is the
 * displayed bar's start, seconds), so a 2-minute detection lands on the 5- or
 * 15-minute candle that contains it. The dot hangs off the DISPLAYED candle's
 * extreme, looked up from the series, so it never sits inside a longer wick.
 * Several markers on one candle and side stack outward.
 */

export interface AbsorptionItem {
  id: string;
  /** Displayed bar start, unix seconds. */
  time: number;
  direction: 'bullish' | 'bearish';
  /** Fallback anchor when the series has no bar at `time`. */
  high: number;
  low: number;
  /** "ABS PW". */
  label: string;
  color: string;
}

interface Placed {
  id: string;
  x: number;
  y: number;
  textX: number;
  textW: number;
  label: string;
  color: string;
}

/** Gap between the candle extreme and the first dot, px. */
export const ABSORPTION_OFFSET = 10;
/** Distance between stacked markers on one candle, px. */
export const ABSORPTION_STACK = 13;
const RADIUS = 3.5;
const FONT_PX = 10;
const HIT_PX = 7;
/** Shared layout: the dot + label box height, and its gap off the candle. */
const BOX_H = 12;
const LAYOUT_GAP = 4;
const LABEL_FONT = `bold ${FONT_PX}px Arial`;

class AbsorptionRenderer implements IPrimitivePaneRenderer {
  private _items: Placed[] = [];

  update(items: Placed[]): void {
    this._items = items;
  }

  draw(target: any): void {
    target.useBitmapCoordinateSpace((scope: any) => {
      const ctx = scope.context as CanvasRenderingContext2D;
      const hr = scope.horizontalPixelRatio as number;
      const vr = scope.verticalPixelRatio as number;
      ctx.save();
      ctx.font = `bold ${FONT_PX * vr}px Arial`;
      ctx.textBaseline = 'middle';
      ctx.textAlign = 'left';
      for (const p of this._items) {
        ctx.fillStyle = p.color;
        ctx.strokeStyle = 'rgba(0,0,0,0.55)';
        ctx.lineWidth = 1 * hr;
        ctx.beginPath();
        ctx.arc(p.x * hr, p.y * vr, RADIUS * hr, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
        ctx.fillText(p.label, p.textX * hr, p.y * vr);
      }
      ctx.restore();
    });
  }
}

class AbsorptionPaneView implements IPrimitivePaneView {
  private _renderer = new AbsorptionRenderer();
  constructor(private _source: AbsorptionMarkerPrimitive) {}
  zOrder(): PrimitivePaneViewZOrder {
    return 'top';
  }
  renderer(): IPrimitivePaneRenderer {
    this._renderer.update(this._source.place());
    return this._renderer;
  }
}

/** Rough text width without a canvas (Arial bold ~0.62em per glyph). */
export function labelWidth(label: string, fontPx = FONT_PX): number {
  return Math.ceil(label.length * fontPx * 0.62);
}

export class AbsorptionMarkerPrimitive implements ISeriesPrimitive<Time> {
  private _items: AbsorptionItem[] = [];
  private _placed: Placed[] = [];
  private _view = new AbsorptionPaneView(this);
  private _requestUpdate?: () => void;
  private _series?: SeriesAttachedParameter<Time>['series'];
  private _chart?: SeriesAttachedParameter<Time>['chart'];
  private _onPlaced?: (placed: { id: string; x: number; y: number }[]) => void;
  private _layout = new MarkerLayoutClient('absorption', (ctx) => this._layoutItems(ctx));

  attached(param: SeriesAttachedParameter<Time>): void {
    this._series = param.series;
    this._chart = param.chart;
    this._requestUpdate = param.requestUpdate;
    this._layout.setAttached(true);
  }

  detached(): void {
    this._layout.setAttached(false);
    this._series = undefined;
    this._chart = undefined;
    this._requestUpdate = undefined;
  }

  /** Join the chart's shared marker layout (null: stack alone). */
  setMarkerLayout(reg: MarkerLayoutRegistry | null): void {
    this._layout.setRegistry(reg);
  }

  /** One box per marker: the dot plus its label to the right. */
  private _layoutItems(ctx: LayoutContext): LayoutItem[] {
    const out: LayoutItem[] = [];
    for (const it of this._items) {
      if (it.time < ctx.fromTime || it.time > ctx.toTime) continue;
      const x = ctx.timeToX(it.time);
      if (x === null) continue;
      const bar = ctx.bar(it.time);
      const bull = it.direction === 'bullish';
      const anchor = ctx.priceToY(bull ? bar?.low ?? it.low : bar?.high ?? it.high);
      if (anchor === null) continue;
      const textW = Math.ceil(ctx.measure(it.label, LABEL_FONT));
      out.push({
        key: it.id,
        time: it.time,
        x,
        side: bull ? 'below' : 'above',
        anchorY: anchor,
        w: RADIUS + 1 + RADIUS + 3 + textW,
        h: BOX_H,
        dx: -(RADIUS + 1),
        gap: LAYOUT_GAP,
        priority: LAYOUT_PRIORITY.absorption,
        color: it.color,
        label: it.label,
        ref: { it, textW },
      });
    }
    return out;
  }

  paneViews(): IPrimitivePaneView[] {
    return [this._view];
  }

  updateAllViews(): void {
    /* positions are recomputed on every render */
  }

  setItems(items: AbsorptionItem[]): void {
    this._items = items;
    this._layout.invalidate();
    this._requestUpdate?.();
  }

  items(): AbsorptionItem[] {
    return this._items;
  }

  /** Told where the markers landed on each render (on-screen ones only). */
  setOnPlaced(cb: ((placed: { id: string; x: number; y: number }[]) => void) | undefined): void {
    this._onPlaced = cb;
  }

  /** Screen positions for the current render (also used for hit testing). */
  place(): Placed[] {
    const out: Placed[] = [];
    if (!this._series || !this._chart) {
      this._placed = out;
      return out;
    }
    const shared = this._layout.placed();
    if (shared) {
      for (const p of shared) {
        const { it, textW } = p.item.ref as { it: AbsorptionItem; textW: number };
        const x = p.item.x;
        out.push({ id: it.id, x, y: p.top + p.h / 2, textX: x + RADIUS + 3, textW, label: it.label, color: it.color });
      }
      this._placed = out;
      this._onPlaced?.(out.map((p) => ({ id: p.id, x: p.x, y: p.y })));
      return out;
    }
    const ts = this._chart.timeScale();
    const stack = new Map<string, number>();
    for (const it of this._items) {
      const x = ts.timeToCoordinate(it.time as Time);
      if (x === null) continue;
      let high = it.high;
      let low = it.low;
      const idx = ts.timeToIndex(it.time as Time, false);
      if (idx !== null) {
        const bar = this._series.dataByIndex(idx as unknown as number, MismatchDirection.None) as
          | { high?: number; low?: number }
          | null;
        if (bar && typeof bar.high === 'number' && typeof bar.low === 'number') {
          high = bar.high;
          low = bar.low;
        }
      }
      const bull = it.direction === 'bullish';
      const yAnchor = this._series.priceToCoordinate(bull ? low : high);
      if (yAnchor === null) continue;
      const key = `${it.time}:${it.direction}`;
      const n = stack.get(key) ?? 0;
      stack.set(key, n + 1);
      const off = ABSORPTION_OFFSET + n * ABSORPTION_STACK;
      const y = bull ? yAnchor + off : yAnchor - off;
      out.push({
        id: it.id,
        x,
        y,
        textX: x + RADIUS + 3,
        textW: labelWidth(it.label),
        label: it.label,
        color: it.color,
      });
    }
    this._placed = out;
    this._onPlaced?.(out.map((p) => ({ id: p.id, x: p.x, y: p.y })));
    return out;
  }

  /** Marker under a point (dot or its label), from the last render. */
  markerAt(x: number, y: number): AbsorptionItem | null {
    let best: Placed | null = null;
    let bestD = Infinity;
    for (const p of this._placed) {
      const inText = x >= p.textX - 1 && x <= p.textX + p.textW && Math.abs(y - p.y) <= FONT_PX / 2 + 2;
      const d = Math.hypot(x - p.x, y - p.y);
      if ((d <= HIT_PX || inText) && d < bestD) {
        best = p;
        bestD = d;
      }
    }
    return best ? this._items.find((i) => i.id === best!.id) ?? null : null;
  }
}

/**
 * Store markers -> primitive items for one chart: base symbol filter, fold the
 * base-bar time into the displayed timeframe, colour by direction.
 */
export function toAbsorptionItems(
  markers: readonly { id: string; timestamp: number; direction: 'bullish' | 'bearish'; high: number; low: number; levelCode: string }[],
  intervalMs: number,
  colors: { bull: string; bear: string },
): AbsorptionItem[] {
  const step = Math.max(60_000, intervalMs);
  return markers.map((m) => ({
    id: m.id,
    time: Math.floor((Math.floor(m.timestamp / step) * step) / 1000),
    direction: m.direction,
    high: m.high,
    low: m.low,
    label: `ABS ${m.levelCode}`,
    color: m.direction === 'bullish' ? colors.bull : colors.bear,
  }));
}
